import { Columns3, Download } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Label } from '~/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { cn } from '~/utils/cn';
import { downloadCsv } from '~/utils/csv';

import type {
  ExportFieldPreset,
  ExportTemplateColumn,
  ExportTemplateDefinition,
} from '../types/export.types';
import { buildColumnManifest } from '../utils/export-column-selection';

import { ExportColumnList } from './export-column-list';

interface ExportColumnPickerProps {
  columns: ExportTemplateColumn[];
  selected: string[];
  onChange: (selected: string[]) => void;
  /** The run's enriched attributes, when known — drives the default and the reset. */
  batchAttributeKeys: readonly string[] | undefined;
  /** The connector's standard starting columns when the run's are unknown. */
  fallbackFields?: readonly string[] | undefined;
  /** Batch attribute keys with no connector column — surfaced, not swallowed. */
  unmatched: string[];
  presets: ExportFieldPreset[];
  /** The active template's encoding delta, for the downloadable manifest. */
  transformations: Record<string, string>;
  fileLabel: string;
  /** The active template, for the descriptor either/or in the list. */
  template: ExportTemplateDefinition | undefined;
  /** Test-id root for E2E; derived ids are `${testId}-trigger-button`, `-popover`, etc. */
  testId?: string;
}

/**
 * Which columns the export contains, in a popover.
 *
 * This was an inline expander, and inside a drawer that was the wrong
 * container: opening it pushed the preview — and the drawer's own footer, with
 * Start export in it — down and out of reach, so choosing columns meant losing
 * sight of the thing the choice was for. A popover overlays instead of
 * reflowing, and unlike a nested sheet it does not stack a second overlay on
 * the drawer that owns it.
 *
 * Draft-then-apply, not live: every committed change re-renders the preview and
 * re-runs the estimate, so 12 ticks landing as one change is the difference
 * between one round trip and twelve. It also gives Cancel a meaning.
 *
 * Nothing is locked: the run's enriched attributes are what the list starts
 * checked with, and every column — identifiers included — can be toggled from
 * there.
 */
export function ExportColumnPicker({
  columns,
  selected,
  onChange,
  batchAttributeKeys,
  fallbackFields,
  unmatched,
  presets,
  transformations,
  fileLabel,
  template,
  testId = 'export-column-picker',
}: ExportColumnPickerProps) {
  const { t } = useTranslation('exports');
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(new Set(selected));
  // The popover is portalled in here rather than to the body: this picker lives
  // inside the export Sheet, and a Radix dialog's scroll lock blocks wheel
  // events for anything outside its own subtree — which left the 51-column list
  // clipped and refusing to scroll.
  //
  // Held in state via a callback ref, not a `useRef`: the container is READ
  // during render, and a ref's `.current` set after mount would not re-render
  // to deliver it (which is also what the refs lint rule is pointing at).
  const [portalEl, setPortalEl] = useState<HTMLDivElement | null>(null);

  /** Opening re-seeds the draft, so a cancelled edit cannot leak into the next open. */
  const handleOpenChange = (next: boolean) => {
    if (next) setDraft(new Set(selected));
    setOpen(next);
  };

  const handleDownloadManifest = () => {
    downloadCsv(
      `${fileLabel}-columns.csv`,
      buildColumnManifest({ columns, selected, transformations }),
      [
        { key: 'key', label: 'Column' },
        { key: 'label', label: 'Label' },
        { key: 'kind', label: 'Kind' },
        { key: 'sourceColumns', label: 'Source column' },
        { key: 'identifier', label: 'Identifier' },
        { key: 'encoding', label: 'Encoding' },
      ],
    );
  };

  return (
    <div
      ref={setPortalEl}
      data-testid={testId}
      className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2"
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Label className="text-sm">{t('estimate.columns.label')}</Label>
        <Badge
          variant="outline"
          data-testid={`${testId}-count-badge`}
          className="pointer-events-none text-[11px] font-normal"
        >
          {t('estimate.columns.count', { selected: selected.length, total: columns.length })}
        </Badge>
        <span className="text-muted-foreground text-xs">
          {batchAttributeKeys && batchAttributeKeys.length > 0
            ? t('estimate.columns.batchHint')
            : fallbackFields && fallbackFields.length > 0
              ? t('estimate.columns.standardHint')
              : t('estimate.columns.allHint')}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid={`${testId}-trigger-button`}
              className="h-8 cursor-pointer text-xs"
            >
              <Columns3 aria-hidden="true" className="mr-1 size-3.5" />
              {t('columns.trigger')}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            container={portalEl}
            data-testid={`${testId}-popover`}
            align="end"
            side="bottom"
            // Radix measures the space between the trigger and the viewport
            // edge and publishes it as `--radix-popover-content-available-height`.
            // Bounding to it is what keeps a 51-column list from running past
            // the bottom of the drawer — a `vh` cap cannot know where the
            // trigger sits, which is why the previous one overflowed. The
            // `28rem` keeps it from becoming absurdly tall on a big screen, and
            // the var's own fallback covers a Radix version that does not set it.
            collisionPadding={12}
            className="flex max-h-[min(28rem,var(--radix-popover-content-available-height,28rem))] w-[min(26rem,90vw)] flex-col gap-0 overflow-hidden p-0"
          >
            {/* No height class on the list: `overflow-hidden` above plus the
                list's own `flex-1 min-h-0` make it the single scroll area, so
                the search box and the footer stay put while the rows move. */}
            <ExportColumnList
              columns={columns}
              draft={draft}
              onDraftChange={setDraft}
              runFields={batchAttributeKeys}
              unmatched={unmatched}
              presets={presets}
              template={template}
            />
            <div className="flex shrink-0 items-center justify-between gap-2 border-t px-4 py-3">
              <span data-testid={`${testId}-draft-count`} className="text-muted-foreground text-xs">
                {t('columns.draftCount', { selected: draft.size, total: columns.length })}
              </span>
              <span className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 cursor-pointer text-xs"
                  data-testid={`${testId}-cancel-button`}
                  onClick={() => {
                    setOpen(false);
                  }}
                >
                  {t('estimate.cancel')}
                </Button>
                {/* Blocked at zero content columns, like the export itself. */}
                <Button
                  type="button"
                  size="sm"
                  className={cn(
                    'h-8 text-xs',
                    draft.size === 0 ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                  )}
                  aria-disabled={draft.size === 0}
                  data-testid={`${testId}-apply-button`}
                  onClick={() => {
                    if (draft.size === 0) return;
                    onChange(
                      columns.filter((column) => draft.has(column.key)).map((column) => column.key),
                    );
                    setOpen(false);
                  }}
                >
                  {t('columns.apply')}
                </Button>
              </span>
            </div>
          </PopoverContent>
        </Popover>

        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 cursor-pointer text-xs"
          data-testid={`${testId}-download-list-button`}
          onClick={handleDownloadManifest}
        >
          <Download aria-hidden="true" className="mr-1 size-3.5" />
          {t('estimate.columns.downloadList')}
        </Button>
      </div>
    </div>
  );
}
